// @flow
import React, { useCallback } from 'react';
import VirtualSelectAsync, { type VirtualSelectAsyncProps } from './VirtualSelectAsync';

import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import type { FormControlProps as MuiFormControlProps } from '@mui/material/FormControl/FormControl';

import { useIntl } from 'react-intl';

type Props = {
  ...VirtualSelectAsyncProps,
  classes: {
    [key: string]: string,
  },
  className: string,
  placeholder: {
    id: string,
    defaultMessage?: string,
    description?: string,
  },
  label: {
    id: string,
    defaultMessage: string,
  },
  value: string,
  defaultLabel: string,
  input: {
    value: string,
    onChange: (value: string) => void,
    name: string,
  },
  required?: boolean,
  readOnly?: boolean,
  capital?: boolean,
  components?: $ReadOnly<{| Group: React$Node |}>,
  selectStyles?: Object,
  disabled?: boolean,
  FormControlProps?: MuiFormControlProps,
};

const MuiVirtualSelectAsync = ({
  classes,
  label,
  input,
  value,
  options,
  readOnly,
  className,
  required,
  placeholder,
  selectStyles = {},
  loadData,
  pageLimit = 50,
  defaultLabel,
  FormControlProps = {},
  ...rest
}: Props) => {
  const { formatMessage } = useIntl();

  const getValue = useCallback(() => {
    return value || input.value || null;
  }, [input.value, value]);

  return (
    <FormControl fullWidth {...FormControlProps}>
      <VirtualSelectAsync
        {...rest}
        className={className}
        value={getValue()}
        onChange={input.onChange}
        placeholder={(placeholder && formatMessage(placeholder)) || ''}
        styles={selectStyles}
        loadData={loadData}
        pageLimit={pageLimit}
        defaultLabel={defaultLabel}
        renderInput={({ ref, onBtnClick, onBtnMouseDown, opened, ...params }) => (
          <TextField
            {...params}
            fullWidth
            label={label || ''}
            required={required}
            inputProps={{
              ref,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={onBtnClick} onMouseDown={onBtnMouseDown} edge="end">
                    <ArrowDropDownIcon
                      fontSize="inherit"
                      sx={{
                        transition: 'transform .3s ease-in-out',
                        transform: `rotate(${!opened ? 0 : '180deg'})`,
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
    </FormControl>
  );
};

export default MuiVirtualSelectAsync;
